<template>
    <div class="space-y-2">
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div
                v-for="gait in gaits"
                :key="gait.key"
                class="bg-dark-light/60 space-y-1 rounded-lg p-3"
                :style="{ borderLeft: `4px solid ${gait.color}` }"
            >
                <div class="flex items-center justify-between text-xs tracking-widest text-white uppercase">
                    <span>{{ $t(`gait.${gait.gait}`) }}</span>
                    <div class="flex items-center space-x-2">
                        <button
                            type="button"
                            class="rounded bg-dark-lighter px-2 py-1 text-xxs text-white"
                            @click="() => nudge(gait.key, -1)"
                            :disabled="isLocked || values[gait.key] <= 0"
                        >
                            -
                        </button>
                        <span class="text-text-base font-semibold">{{ formatSeconds(values[gait.key]) }}</span>
                        <button
                            type="button"
                            class="rounded bg-dark-lighter px-2 py-1 text-xxs text-white"
                            @click="() => nudge(gait.key, 1)"
                            :disabled="isLocked || (!remaining && remaining !== 0 ? false : remaining === 0)"
                        >
                            +
                        </button>
                    </div>
                </div>
                <input
                    type="range"
                    :min="0"
                    :max="values[gait.key] + remaining"
                    :step="1"
                    :value="values[gait.key]"
                    @touchstart.stop
                    @touchmove.stop
                    @input="(e) => onSlider(gait.key, Number(e.target.value))"
                    :disabled="isLocked"
                    class="accent-brand-base w-full"
                    :style="{ touchAction: 'none' }"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import { secondsToHMS } from '@/lib/helpers/convert'
import { GAIT_TIME_KEYS, wholeSeconds } from '@/lib/sessions/gait-total'
import tailwind from '@/lib/tailwind'
import { useGaitAllocation } from './useGaitAllocation'

const props = defineProps({
    totalSeconds: { type: Number, default: 0 },
    modelValue: {
        type: Object,
        default: () => ({ walk_time: 0, trot_time: 0, canter_time: 0 }),
    },
    // Declared gaits only cover walk, trot and canter: hide (and never emit) standing.
    showStanding: { type: Boolean, default: true },
})
const emits = defineEmits(['update:modelValue'])

const gaits = [
    { key: 'standing_time', gait: 'standing', color: tailwind.theme.color.gaits.standing },
    { key: 'walk_time', gait: 'walk', color: tailwind.theme.color.gaits.walk },
    { key: 'trot_time', gait: 'trot', color: tailwind.theme.color.gaits.trot },
    { key: 'canter_time', gait: 'canter', color: tailwind.theme.color.gaits.canter },
].filter((gait) => props.showStanding || gait.key !== 'standing_time')

const gaitKeys = GAIT_TIME_KEYS.filter((key) => gaits.some((gait) => gait.key === key))

const totalRef = computed(() => props.totalSeconds)
const emitCurrent = (vals) => {
    emits('update:modelValue', Object.fromEntries(gaitKeys.map((key) => [key, wholeSeconds(vals[key])])))
}

const { values, totalSafe, isLocked, remaining, onSlider, nudge, enforceLimits } = useGaitAllocation(
    gaits.map((g) => g.key),
    totalRef,
    emitCurrent
)

enforceLimits()
emitCurrent(values)

watch(
    () => props.modelValue,
    (val) => {
        for (const key of gaitKeys) {
            values[key] = Math.max(0, val?.[key] ?? 0)
        }
        enforceLimits()
        emitCurrent(values)
    },
    { immediate: true, deep: true }
)

const formatSeconds = (sec) => secondsToHMS(sec, 3, true)
</script>
